/**
 * User Service
 * 
 * 處理用戶相關的 API 請求和資料操作
 * 
 * =================================================================
 * 對外提供的方法 (Public Methods)
 * =================================================================
 * 
 * 【用戶創建與註冊】
 * - createUserByAdmin(userData: UserFormData): Promise<ServiceResult>
 *   功用: 管理員創建新用戶（包含 Firebase Auth 帳戶）
 *   操作: 後端操作 (Cloud Functions)
 *   輸入: { email, displayName?, role?, organizationId? }
 *   輸出: { success, user?, error? }
 *   權限: admin, owner
 * 
 * 【用戶資料查詢】
 * - getUserProfile(uid: string): Promise<UserProfile | null>
 *   功用: 獲取指定用戶的完整資料
 *   操作: 前端操作 (Firestore SDK)
 *   輸入: 用戶 UID
 *   輸出: UserProfile 物件或 null
 *   權限: 任何已認證用戶
 * 
 * - getUsers(): Promise<UserProfile[]>
 *   功用: 獲取同組織內的所有用戶列表（過濾/排序/分頁由前端 computed 處理）
 *   操作: 前端操作 (Firestore SDK)
 *   輸出: UserProfile 陣列
 *   權限: admin, owner
 * 
 
 * 【用戶資料更新】
 * - updateUserProfile(uid: string, updates: Partial<UserProfile>): Promise<ServiceResult>
 *   功用: 更新用戶基本資料（排除敏感欄位）
 *   操作: 前端操作 (Firestore SDK)
 *   輸入: UID + 更新欄位（displayName, groupIds 等）
 *   輸出: { success, error? }
 *   權限: 本人或 admin, owner
 * 
 
 * 【用戶管理操作】
 * - deleteUser(uid: string): Promise<ServiceResult>
 *   功用: 軟刪除用戶（設為 disabled）
 *   操作: 前端操作 (Firestore SDK)
 *   輸入: 用戶 UID
 *   輸出: { success, error? }
 *   權限: owner
 * 
 * - restoreUser(uid: string): Promise<ServiceResult>
 *   功用: 恢復已刪除的用戶
 *   操作: 前端操作 (Firestore SDK)
 *   輸入: 用戶 UID
 *   輸出: { success, error? }
 *   權限: owner
 * 
 * 【密碼管理】
 * - sendUserPasswordResetEmail(email: string): Promise<ServiceResult>
 *   功用: 發送密碼重設郵件
 *   操作: 前端操作 (Firebase Auth SDK)
 *   輸入: 用戶 Email
 *   輸出: { success, message }
 *   權限: admin, owner
 * 
 * 【批量操作】
 * - importUsersFromCSV(csvContent: string, organizationId: string): Promise<CSVImportResult>
 *   功用: 從 CSV 批量導入用戶
 *   操作: 後端操作 (透過 Cloud Functions 批量創建)
 *   輸入: CSV 內容字串 + 組織ID
 *   輸出: { success, totalRows, successCount, errorCount, errors }
 *   權限: admin, owner
 * 
 * 【權限檢查】
 * - checkUserManagementPermission(currentUserRole: string): Promise<UserManagementPermission>
 *   功用: 檢查用戶管理權限
 *   操作: 前端操作 (純邏輯運算)
 *   輸入: 當前用戶角色
 *   輸出: 權限物件 { canView, canCreate, canEdit, canDelete, canManageRoles, canBulkImport }
 *   權限: 任何已認證用戶
 * 
 * =================================================================
 * 權限說明:
 * - owner: 擁有全部權限
 * - admin: 無法刪除用戶、變更角色
 * - member: 僅能查看和更新自己的基本資料
 * =================================================================
 */

import { httpsCallable } from 'firebase/functions'
import { doc, getDoc, updateDoc, serverTimestamp, query, collection, where, getDocs } from 'firebase/firestore'
import { sendPasswordResetEmail } from 'firebase/auth'
import { functions, db, auth } from '../config/firebase'
import { useAuthStore } from '../stores/auth'
import { getDepartment } from './departmentService'

// ================================ 
// Interfaces
// ================================ 

export interface UserProfile {
  uid: string
  email: string
  displayName?: string | null
  role: 'owner' | 'admin' | 'member'
  organizationId?: string | null
  departmentId?: string | null
  groupIds: string[]
  disabled: boolean
  createdAt: any // Firestore Timestamp
  updatedAt: any // Firestore Timestamp
  deletedAt?: any | null // Firestore Timestamp
  /** 平台管理員標記，由 Firebase console 手動設定 */
  isPlatformAdmin?: boolean
  /** 代理人設定：delegateUserId 於 startAt~endAt 期間可代為簽核本人的待辦 */
  delegation?: { delegateUserId: string; startAt: any; endAt: any } | null
}

export interface ServiceResult {
  success: boolean
  user?: UserProfile
  error?: string
  [key: string]: any // Allow other properties
}


export interface UserFormData {
  email: string
  displayName?: string
  role: 'owner' | 'admin' | 'member'
  organizationId: string
  departmentId?: string
  groupIds?: string[]
}

export interface CSVImportResult {
  success: boolean
  totalRows: number
  successCount: number
  errorCount: number
  errors: Array<{
    row: number
    email: string
    error: string
  }> 
}

interface UserManagementPermission {
  canView: boolean
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
  canManageRoles: boolean
  canBulkImport: boolean
}

// For batch creation
export interface BatchUserCreationResult {
  success: boolean;
  email: string;
  uid?: string;
  error?: string;
  message: string;
}

export interface BatchCreationSummary {
  total: number;
  successCount: number;
  errorCount: number;
}

export interface CreateUsersInBatchResult {
  success: boolean;
  results: BatchUserCreationResult[];
  summary: BatchCreationSummary;
  message: string;
}

// ================================ 
// Validation Functions
// ================================ 

/**
 * 驗證部門是否屬於指定組織
 */
const validateDepartmentInOrganization = async (departmentId: string, organizationId: string): Promise<boolean> => {
  try {
    if (!departmentId || !organizationId) {
      return false
    }
    
    const department = await getDepartment(departmentId)
    return department ? department.organizationId === organizationId : false
  } catch (error: any) {
    console.error('驗證部門所屬組織失敗:', error)
    return false
  }
}

// ================================ 
// Functions
// ================================ 

/**
 * 管理員創建用戶（包含 Firebase Auth 帳戶和 Firestore 資料）
 * Backend Operation: 使用 Cloud Functions 'createUserByAdmin'
 */
export const createUserByAdmin = async (userData: {
  email: string
  displayName?: string
  role?: 'member' | 'admin'
  organizationId?: string
  departmentId?: string | null
}): Promise<ServiceResult> => {
  try {
    // 驗證部門是否屬於同一組織
    if (userData.departmentId && userData.organizationId) {
      const isValidDepartment = await validateDepartmentInOrganization(
        userData.departmentId, 
        userData.organizationId
      )
      if (!isValidDepartment) {
        throw new Error('所選部門不屬於當前組織')
      }
    }
    
    console.log('👤 透過 Cloud Functions 管理員創建用戶:', userData)
    const createUserByAdminFn = httpsCallable(functions, 'createUserByAdmin')
    const result = await createUserByAdminFn(userData)
    console.log('✅ 管理員創建用戶成功')
    return result.data as ServiceResult
  } catch (error: any) {
    console.error('管理員創建用戶失敗:', error)
    if (error.details && error.details.originalCode) {
      const errorWithCode = new Error(error.message)
      ;(errorWithCode as any).code = error.details.originalCode
      throw errorWithCode
    }
    throw new Error(error.message || 'Failed to create user')
  }
}

/**
 * 獲取用戶資料從 Firestore
 */
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    console.log('🔍 獲取 Firestore 用戶資料:', uid)
    const userDocRef = doc(db, 'users', uid)
    const userDocSnap = await getDoc(userDocRef)
    
    if (userDocSnap.exists()) {
      const userData = userDocSnap.data()
      console.log('✅ 找到 Firestore 用戶資料')
      return {
        ...userData,
        createdAt: userData.createdAt?.toDate?.() || userData.createdAt,
        updatedAt: userData.updatedAt?.toDate?.() || userData.updatedAt
      } as UserProfile
    } else {
      console.warn(`❌ 用戶文件不存在: ${uid}`)
      return null
    }
  } catch (error: any) {
    console.error('獲取用戶資料失敗:', error)
    throw new Error(`獲取用戶資料失敗: ${error.message}`)
  }
}

/**
 * 組織成員姓名快取：整個組織一次撈回（displayName 變動頻率低，getUsers() 本來就是全件查詢），
 * 之後全部命中，不再逐一 get by uid。沒有 TTL，跟著分頁存活，重整即清空；
 * 同時發生的多次呼叫共用同一個 in-flight Promise，避免重複觸發。詳見 docs/instance-list-cache.md。
 */
let orgProfileCache: Map<string, { displayName: string; email: string }> | null = null
let orgProfileFetchPromise: Promise<Map<string, { displayName: string; email: string }>> | null = null

export const getOrgUserProfiles = async (): Promise<Map<string, { displayName: string; email: string }>> => {
  if (orgProfileCache) return orgProfileCache
  if (orgProfileFetchPromise) return orgProfileFetchPromise

  orgProfileFetchPromise = getUsers().then(users => {
    const map = new Map<string, { displayName: string; email: string }>()
    users.forEach(user => {
      map.set(user.uid, { displayName: user.displayName || user.email || user.uid, email: user.email })
    })
    orgProfileCache = map
    orgProfileFetchPromise = null
    return map
  })

  return orgProfileFetchPromise
}

/**
 * 更新用戶資料（包含角色修改）
 */
export const updateUserProfile = async (uid: string, updates: Partial<UserProfile>): Promise<ServiceResult> => {
  try {
    console.log('🔧 更新 Firestore 用戶資料:', { uid, updates });
    
    // 獲取當前登入用戶的資料
    const authStore = useAuthStore();
    const currentUser = authStore.userProfile;
    
    if (!currentUser) {
      throw new Error('用戶未登入');
    }
    
    // 獲取目標用戶的資料
    const targetUser = await getUserProfile(uid);
    if (!targetUser) {
      throw new Error('用戶不存在');
    }
    
    // 檢查角色修改權限
    if (updates.role && updates.role !== targetUser.role) {
      // 只有 Owner 才能修改角色
      if (currentUser.role !== 'owner') {
        throw new Error('權限不足：只有組織擁有者可以修改用戶角色');
      }
      
      // 不能修改自己的角色
      if (currentUser.uid === uid) {
        throw new Error('無法修改自己的角色');
      }
      
      // 檢查是否為同一組織
      if (currentUser.organizationId !== targetUser.organizationId) {
        throw new Error('只能修改同組織用戶的角色');
      }
      
      // 不能修改 Owner 的角色
      if (targetUser.role === 'owner') {
        throw new Error('無法修改組織擁有者的角色');
      }
    }
    
    // 如果更新包含 departmentId，需要驗證部門是否屬於用戶的組織
    if (updates.departmentId) {
      if (targetUser.organizationId) {
        const isValidDepartment = await validateDepartmentInOrganization(
          updates.departmentId, 
          targetUser.organizationId
        );
        if (!isValidDepartment) {
          throw new Error('所選部門不屬於用戶的組織');
        }
      }
    }
    
    // 準備更新資料，排除不可修改的敏感欄位
    const allowedUpdates = { ...updates };
    delete allowedUpdates.email;
    delete allowedUpdates.organizationId;
    delete allowedUpdates.uid;

    const userDocRef = doc(db, 'users', uid);
    const updateData = { ...allowedUpdates, updatedAt: serverTimestamp() };

    await updateDoc(userDocRef, updateData);
    console.log('✅ Firestore 用戶資料更新成功');
    return { success: true };
  } catch (error: any) {
    console.error('更新用戶資料失敗:', error);
    throw new Error(`更新用戶資料失敗: ${error.message}`);
  }
};


/**
 * 獲取同組織的所有用戶列表（全件取得，過濾/排序/分頁由前端 computed 處理）
 */
export const getUsers = async (): Promise<UserProfile[]> => {
  const authStore = useAuthStore()
  const organizationId = authStore.organizationId

  if (!organizationId) throw new Error('用戶未屬於任何組織')

  // 僅以 organizationId 過濾，其餘交由前端處理
  const q = query(collection(db, 'users'), where('organizationId', '==', organizationId))
  const querySnapshot = await getDocs(q)

  const users: UserProfile[] = []
  querySnapshot.forEach((doc) => {
    const userData = doc.data()
    users.push({
      ...userData,
      id: userData.uid,
      createdAt: userData.createdAt?.toDate?.() || userData.createdAt,
      updatedAt: userData.updatedAt?.toDate?.() || userData.updatedAt,
      deletedAt: userData.deletedAt?.toDate?.() || userData.deletedAt
    } as UserProfile & { id: string })
  })

  return users
}

/**
 * 停用用戶（同步停用 Firebase Auth 及 Firestore）
 * Backend Operation: 使用 Cloud Functions 'disableUserAccount'
 */
export const deleteUser = async (uid: string): Promise<ServiceResult> => {
  const disableUserAccountFn = httpsCallable(functions, 'disableUserAccount')
  const result = await disableUserAccountFn({ uid })
  return result.data as ServiceResult
}

/**
 * 恢復已停用的用戶（同步啟用 Firebase Auth 及 Firestore）
 * Backend Operation: 使用 Cloud Functions 'enableUserAccount'
 */
export const restoreUser = async (uid: string): Promise<ServiceResult> => {
  const enableUserAccountFn = httpsCallable(functions, 'enableUserAccount')
  const result = await enableUserAccountFn({ uid })
  return result.data as ServiceResult
}

/**
 * 軟刪除用戶（刪除 Firebase Auth 帳號，Firestore 保留紀錄）
 * Backend Operation: 使用 Cloud Functions 'softDeleteUser'
 */
export const softDeleteUser = async (uid: string): Promise<ServiceResult> => {
  const softDeleteUserFn = httpsCallable(functions, 'softDeleteUser')
  const result = await softDeleteUserFn({ uid })
  return result.data as ServiceResult
}

/**
 * 批量創建用戶（透過 Cloud Functions）
 * Backend Operation: 使用 Cloud Functions 'createUsersInBatch'
 */
const createUsersInBatch = async (
  users: Omit<UserProfile, 'uid' | 'createdAt' | 'updatedAt' | 'disabled' | 'groupIds'>[],
  organizationId: string
): Promise<CreateUsersInBatchResult> => {
  try {
    console.log('📦 透過 Cloud Functions 批量創建用戶:', users.length)
    const createUsersInBatchFn = httpsCallable(functions, 'createUsersInBatch')
    const result = await createUsersInBatchFn({ users, organizationId })
    console.log('✅ Cloud Functions 批量創建用戶完成')
    return result.data as CreateUsersInBatchResult
  } catch (error: any) {
    console.error('批量創建用戶失敗:', error)
    throw new Error(`批量創建用戶失敗: ${error.message}`)
  }
}



/**
 * 檢查用戶管理權限
 */
export const checkUserManagementPermission = async (
  currentUserRole: 'owner' | 'admin' | 'member'
): Promise<UserManagementPermission> => {
  const permissions: Record<string, UserManagementPermission> = {
    owner: { canView: true, canCreate: true, canEdit: true, canDelete: true, canManageRoles: true, canBulkImport: true },
    admin: { canView: true, canCreate: true, canEdit: true, canDelete: false, canManageRoles: false, canBulkImport: true },
    member: { canView: false, canCreate: false, canEdit: false, canDelete: false, canManageRoles: false, canBulkImport: false }
  }
  return permissions[currentUserRole] || permissions.member
}

/**
 * 發送密碼重設 Email
 */
export const sendUserPasswordResetEmail = async (email: string): Promise<ServiceResult> => {
  try {
    console.log('📧 發送密碼重設 Email:', email)
    await sendPasswordResetEmail(auth, email)
    console.log('✅ 密碼重設 Email 發送成功')
    return { 
      success: true, 
      message: '密碼重設連結已寄送至用戶信箱（如果該 Email 存在帳戶）'
    }
  } catch (error: any) {
    console.error('發送密碼重設 Email 失敗:', error)
    // Firebase 會對所有 Email 都回傳成功訊息，以避免洩漏用戶資訊
    // 所以這裡也直接回傳成功，保持一致的安全行為
    return { 
      success: true, 
      message: '密碼重設連結已寄送至用戶信箱（如果該 Email 存在帳戶）'
    }
  }
}

/**
 * CSV 批量導入用戶
 * Backend Operation: 使用 Cloud Functions 'createUsersInBatch' 進行批量創建
 */
export const importUsersFromCSV = async (
  csvContent: string,
  organizationId: string
): Promise<CSVImportResult> => {
  try {
    console.log('📥 開始 CSV 批量導入用戶')
    const lines = csvContent.split('\n').filter(line => line.trim())
    if (lines.length <= 1) throw new Error('CSV 文件為空或只有標題行');
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
    const requiredFields = ['email']
    const missingFields = requiredFields.filter(field => !headers.includes(field))
    if (missingFields.length > 0) throw new Error(`CSV 缺少必要欄位: ${missingFields.join(', ')}`)

    const results: CSVImportResult = {
      success: false,
      totalRows: lines.length - 1,
      successCount: 0,
      errorCount: 0,
      errors: []
    }

    const usersData: Array<{ email: string; displayName?: string | null; role: 'member' | 'admin' }> = []
    const emailIndex = headers.indexOf('email')
    const nameIndex = headers.indexOf('displayname') > -1 ? headers.indexOf('displayname') : headers.indexOf('name')
    const roleIndex = headers.indexOf('role')

    for (let i = 1; i < lines.length; i++) {
      try {
        const values = lines[i].split(',').map(v => v.trim())
        const email = values[emailIndex]
        if (!email) throw new Error('Email 欄位為必填')

        usersData.push({
          email,
          displayName: nameIndex > -1 ? values[nameIndex] || null : null,
          role: roleIndex > -1 && (values[roleIndex] === 'admin') ? 'admin' : 'member'
        })
      } catch (rowError: any) {
        results.errorCount++
        results.errors.push({ row: i + 1, email: lines[i].split(',')[0] || '', error: rowError.message })
      }
    }

    if (usersData.length > 0) {
      try {
        const batchResult = await createUsersInBatch(usersData, organizationId)
        results.successCount = batchResult.summary.successCount
        results.errorCount += batchResult.summary.errorCount

        batchResult.results.forEach(res => {
          if (!res.success) {
            results.errors.push({ row: 0, email: res.email, error: res.error || '未知錯誤' })
          }
        })

      } catch (batchError: any) {
        console.error('批量創建用戶失敗:', batchError)
        results.errorCount += usersData.length
        results.errors.push({ row: 0, email: '批量處理', error: batchError.message })
      }
    }

    results.success = results.errorCount === 0
    console.log(`✅ CSV 導入完成: 成功 ${results.successCount}, 失敗 ${results.errorCount}`)
    return results
  } catch (error: any) {
    console.error('CSV 導入失敗:', error)
    throw new Error(`CSV 導入失敗: ${error.message}`)
  }
}